<template>
  <DefaultCrudView
    @create-new="newACL"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">
      <v-data-table
        :headers="headers"
        :items="acls"
        class="elevation-1"
        :loading="loading"
        hide-default-footer
        :items-per-page="-1"
      >
        <template v-slot:item.title="{ item }">
          {{ getEntityTitle(item) }}
        </template>

        <template v-slot:item.acl="{ item }">
          <div class="pool-acl-chips">
            <v-chip v-for="(aclChip, index) in getAclChips(item.acl)" :key="index" small>
              {{ aclChip.text }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" class="mr-2" @click="editACL(item)">mdi-pencil</v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="deleteACL(item)">mdi-delete</v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>
        </template>
      </v-data-table>

      <!-- Create/Edit Form Modal -->
      <DefaultModal :title="getFormModalTitle()" ref="formModal" @submit="saveACL">
        <template v-slot:content>
          <v-container>
            <v-row>
              <!-- Combined Group/User Dropdown with Filter -->
              <v-col cols="12">
                <v-autocomplete
                  v-model="selectedEntity"
                  :items="combinedEntities"
                  :label="$t('group_or_user')"
                  :filter="filterEntities"
                  item-text="display"
                  item-value="key"
                  :rules="[v => !!v || $t('field_required')]"
                  outlined
                  clearable
                  :attach="false"
                  :menu-props="{
                    maxHeight: 300,
                    contentClass: 'entity-select-menu'
                  }"
                >
                  <template v-slot:item="{ item }">
                    <v-list-item-avatar :class="item.type === 'group' ? 'group-bg' : 'avatar'">
                      <v-icon color="secondary">
                        {{ item.type === 'group' ? 'mdi-account-multiple' : 'mdi-account' }}
                      </v-icon>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title>{{ item.display }}</v-list-item-title>
                      <v-list-item-subtitle v-if="item.email">{{ item.email }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </template>
                  <template v-slot:selection="{ item }">
                    <v-chip color="primary">
                      <v-avatar left class="avatar">
                        <v-icon color="secondary" small>
                          {{ item.type === 'group' ? 'mdi-account-multiple' : 'mdi-account' }}
                        </v-icon>
                      </v-avatar>
                      {{ item.display }}
                    </v-chip>
                  </template>
                </v-autocomplete>
              </v-col>

              <!-- Exclusive Access Control (Radio) -->
              <v-col cols="12">
                <v-radio-group
                  v-model="selectedViewPermission"
                  :label="$t('view_permission')"
                  :rules="[v => !!v || $t('field_required')]"
                  mandatory
                >
                  <v-radio
                    :label="$t('view_all_lanes_with_details')"
                    :value="4"
                    color="primary"
                  ></v-radio>
                  <v-radio
                    :label="$t('view_all_lanes_without_details')"
                    :value="8"
                    color="info"
                  ></v-radio>
                  <v-radio
                    :label="$t('view_lanes_with_my_own_jobs')"
                    :value="16"
                    color="warning"
                  ></v-radio>
                  <v-radio
                    :label="$t('monitor_job_in_list_view')"
                    :value="2048"
                    color="primary"
                  ></v-radio>
                </v-radio-group>
              </v-col>

              <!-- Optional Access Controls (Checkboxes) -->
              <v-col cols="12">
                <v-label>{{ $t('additional_permissions') }}</v-label>
                <v-checkbox
                  v-model="canSendJobs"
                  :label="$t('sent_job_to_pool')"
                  hide-details
                  class="mt-2"
                ></v-checkbox>
                <v-checkbox
                  v-model="canActInAllLanes"
                  :label="$t('act_in_all_lanes')"
                  hide-details
                  class="mt-2"
                ></v-checkbox>
              </v-col>

              <v-col cols="12" class="mt-4">
                <v-label>{{ $t('subtask_permissions') }}</v-label>
                <v-checkbox
                  v-model="canCreateSubtask"
                  :label="$t('create_subtask')"
                  hide-details
                  class="mt-2"
                ></v-checkbox>
                <v-checkbox
                  v-model="canViewSubtasks"
                  :label="$t('view_subtasks')"
                  hide-details
                  class="mt-2"
                ></v-checkbox>
              </v-col>

              <v-col cols="12" class="mt-4">
                <v-label>{{ $t('deliverable_permissions') }}</v-label>
                <v-checkbox
                  v-model="canViewDeliverables"
                  :label="$t('view_deliverables')"
                  hide-details
                  class="mt-2"
                ></v-checkbox>
                <v-checkbox
                  v-model="canEditDeliverables"
                  :label="$t('edit_deliverables')"
                  hide-details
                  class="mt-2"
                ></v-checkbox>
              </v-col>

              <v-col cols="12" class="mt-4">
                <v-label>{{ $t('markup_permission') }}</v-label>
                <v-checkbox
                  v-model="canEditMarkup"
                  :label="$t('edit_markup')"
                  hide-details
                  class="mt-2"
                ></v-checkbox>
              </v-col>

              <v-col cols="12" class="mt-4">
                <v-label>{{ $t('job_permissions') }}</v-label>
                <v-checkbox
                  v-model="canFinishJob"
                  :label="$t('finish_job')"
                  hide-details
                  class="mt-2"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-container>
        </template>
      </DefaultModal>
    </template>
  </DefaultCrudView>
</template>

<style scoped>
.pool-acl-chips {
  display: flex;
  flex-wrap: wrap;
  column-gap: 4px;
  row-gap: 8px;
  padding: 4px 0;
}
</style>

<script>
import { mapActions } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'

import AppActions from '@/store/app/actions-types'

import PoolACLAPI from '@/services/api/job/pool_acl'
import GroupAPI from '@/services/api/core/group'
import UserAPI from '@/services/api/core/user'

const ACL_VIEW_IN_LIST = 2048

export default {
  name: 'PoolACL',

  components: {
    DefaultCrudView,
    DefaultModal
  },

  props: {
    poolId: {
      type: [Number, String, null],
      required: false,
      default: null
    },
    poolTitle: {
      type: String,
      required: false,
      default: ''
    }
  },

  data() {
    return {
      loading: false,
      acls: [],
      selectedACL: null,
      userGroups: [],
      users: [],
      // Form fields
      selectedEntity: null,
      selectedViewPermission: null,
      canSendJobs: false,
      canActInAllLanes: false,
      canCreateSubtask: false,
      canViewSubtasks: false,
      canViewDeliverables: false,
      canEditDeliverables: false,
      canEditMarkup: false,
      canFinishJob: false,
    }
  },

  computed: {
    computedPoolId() {
      return this.poolId || this.$route.params.poolId
    },

    computedPoolTitle() {
      return this.poolTitle || this.$route.query.poolTitle || ''
    },

    breadcrumbs() {
      return [
        {
          text: this.$t('pools'),
          prependIcon: 'mdi-view-sequential',
          iconColor: 'primary',
          to: {
            name: "JobPools",
            params: { appId: this.$route.params.appId }
          },
          disabled: false,
          exact: true,
          ripple: true,
        },
        {
          text: `${this.$t('pool_acls')}: ${this.computedPoolTitle}`,
          prependIcon: 'mdi-shield-account',
        },
      ]
    },

    crudActions() {
      return [
        {
          text: "add",
          eventToEmit: "create-new",
        }
      ]
    },

    headers() {
      return [
        { text: this.$t('group_user'), value: 'title', sortable: true },
        { text: this.$t('access_control'), value: 'acl', sortable: false },
        { text: this.$t('actions'), value: 'actions', sortable: false, width: '120px' }
      ]
    },

    combinedEntities() {
      const groups = this.userGroups.map(g => ({
        key: `group_${g.user_group_id}`,
        display: g.group_title,
        type: 'group',
        id: g.user_group_id,
        email: null
      }))

      const users = this.users.map(u => ({
        key: `user_${u.users_id}`,
        display: u.name,
        type: 'user',
        id: u.users_id,
        email: u.email || null
      }))

      return [...groups, ...users]
    }
  },

  mounted() {
    this.loadData()
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE
    ]),

    async loadData() {
      this.loading = true

      try {
        // Load user groups for the dropdown
        const groupsResult = await GroupAPI.getAllGroups({
          order: 'group_title',
          direction: 'ASC',
          offset: 0,
          limit: 2000000000,
          'filter[include_foreign]': true,
          'filter[active]': true,
        })

        const items = groupsResult.data.items || []
        this.userGroups = items.map(g => ({
          user_group_id: g.user_group_id || g.id,
          group_title: g.group_title || g.title || g.name
        }))

        // Load users for the dropdown
        const usersResult = await UserAPI.getAllUsers({
          order: 'u.name',
          direction: 'ASC',
          offset: 0,
          limit: 2000000000,
          'filter[include_foreign]': true,
          'filter[active]': true,
        })

        const userItems = usersResult.data.items || []
        this.users = userItems.map(u => ({
          users_id: u.users_id || u.id,
          name: u.name,
          email: u.email || null
        }))

        // Validate poolId before loading ACLs
        if (!this.computedPoolId && this.computedPoolId !== 0) {
          console.warn('Invalid poolId provided:', this.computedPoolId)
          this.acls = []
          this.loading = false
          return
        }

        // Load ACLs from pool
        const result = await PoolACLAPI.getPoolACLsFromPool(this.computedPoolId)
        this.acls = result.data.items || []
      } catch (error) {
        console.error('Error loading pool ACLs:', error)
        this.acls = []
      } finally {
        this.loading = false
      }
    },

    newACL() {
      this.selectedACL = null
      this.resetFormFields()
      this.$refs.formModal.openModal()
    },

    editACL(acl) {
      this.selectedACL = acl
      this.populateFormFields(acl)
      this.$refs.formModal.openModal()
    },

    resetFormFields() {
      this.selectedEntity = null
      this.selectedViewPermission = 4 // Default to view_all_lanes_with_details
      this.canSendJobs = false
      this.canActInAllLanes = false
      this.canCreateSubtask = false
      this.canViewSubtasks = false
      this.canViewDeliverables = false
      this.canEditDeliverables = false
      this.canEditMarkup = false
      this.canFinishJob = false
    },

    populateFormFields(acl) {
      // Set entity (group or user)
      if (acl.user_group_id) {
        this.selectedEntity = `group_${acl.user_group_id}`
      } else if (acl.users_id) {
        this.selectedEntity = `user_${acl.users_id}`
      }

      // Extract ACL permissions
      const aclValue = acl.acl || 0

      // Extract view permission (exclusive)
      if (aclValue & 4) {
        this.selectedViewPermission = 4
      } else if (aclValue & 8) {
        this.selectedViewPermission = 8
      } else if (aclValue & 16) {
        this.selectedViewPermission = 16
      } else if (aclValue & ACL_VIEW_IN_LIST) {
        this.selectedViewPermission = ACL_VIEW_IN_LIST
      } else {
        this.selectedViewPermission = 4 // Default
      }

      // Extract optional permissions
      this.canSendJobs = !!(aclValue & 1)
      this.canActInAllLanes = !!(aclValue & 2)
      this.canCreateSubtask = !!(aclValue & 32)
      this.canViewSubtasks = !!(aclValue & 64)
      this.canEditDeliverables = !!(aclValue & 128)
      this.canViewDeliverables = !!(aclValue & 256)
      this.canEditMarkup = !!(aclValue & 512)
      this.canFinishJob = !!(aclValue & 1024)
    },

    saveACL() {
      // Validate required fields
      if (!this.selectedEntity) {
        this.$refs.formModal.errorMessage = this.$t('field_required')
        return
      }

      if (!this.selectedViewPermission) {
        this.$refs.formModal.errorMessage = this.$t('view_permission_required')
        return
      }

      this.$refs.formModal.errorMessage = null
      this.$refs.formModal.errorList = []

      // Parse selected entity
      const entityParts = this.selectedEntity.split('_')
      const entityType = entityParts[0]
      const entityId = parseInt(entityParts[1])

      // Calculate ACL value
      let aclValue = this.selectedViewPermission || 0
      if (this.canSendJobs) aclValue |= 1
      if (this.canActInAllLanes) aclValue |= 2
      if (this.canCreateSubtask) aclValue |= 32
      if (this.canViewSubtasks) aclValue |= 64
      if (this.canEditDeliverables) aclValue |= 128
      if (this.canViewDeliverables) aclValue |= 256
      if (this.canEditMarkup) aclValue |= 512
      if (this.canFinishJob) aclValue |= 1024

      // Build form data
      const formData = {
        pool_id: this.computedPoolId,
        acl: aclValue
      }

      if (entityType === 'group') {
        formData.user_group_id = entityId
        formData.users_id = null
      } else {
        formData.users_id = entityId
        formData.user_group_id = null
      }

      let apiCall
      if (this.selectedACL) {
        const id = this.selectedACL.pool_acl_id || this.selectedACL.id
        apiCall = PoolACLAPI.updatePoolACL(id, formData)
      } else {
        apiCall = PoolACLAPI.createPoolACL(formData)
      }

      apiCall
        .then(() => {
          const message = this.selectedACL ? 'Pool ACL updated successfully' : 'Pool ACL created successfully'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.formModal.closeModal()
          this.loadData()
        })
        .catch((error) => {
          this.$refs.formModal.submitting = false
          if (error.response && error.response.data) {
            this.$refs.formModal.errorMessage = error.response.data.message || 'An error occurred'
          }
        })
    },

    deleteACL(item) {
      if (confirm(this.$t('confirm_delete'))) {
        const id = item.pool_acl_id || item.id
        PoolACLAPI.deletePoolACL(id)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Pool ACL deleted successfully'))
            this.loadData()
          })
          .catch((error) => {
            console.error('Error deleting pool ACL:', error)
          })
      }
    },

    getFormModalTitle() {
      const action = this.selectedACL ? this.$t('edit') : this.$t('add')
      return `${action} ${this.$t('pool_acl')}`
    },

    getEntityTitle(item) {
      if (item.user_group_id) {
        const ug = this.userGroups.find(ug => (ug.user_group_id == item.user_group_id))
        return ug ? ug.group_title : `Group ${item.user_group_id}`
      } else if (item.users_id) {
        const user = this.users.find(u => (u.users_id == item.users_id))
        return user ? user.name : `User ${item.users_id}`
      }
      return 'Unknown'
    },

    getAclChips(aclValue) {
      if (!aclValue) return []

      const chips = []
      const aclTexts = {
        1: { text: this.$t('sent_job_to_pool') },
        2: { text: this.$t('act_in_all_lanes') },
        4: { text: this.$t('view_all_lanes_with_details') },
        8: { text: this.$t('view_all_lanes_without_details') },
        16: { text: this.$t('view_lanes_with_my_own_jobs') },
        32: { text: this.$t('create_subtask') },
        64: { text: this.$t('view_subtasks') },
        128: { text: this.$t('edit_deliverables') },
        256: { text: this.$t('view_deliverables') },
        512: { text: this.$t('edit_markup') },
        1024: { text: this.$t('finish_job') },
        [ACL_VIEW_IN_LIST]: { text: this.$t('monitor_job_in_list_view') }
      }

      const flags = [1, 2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, ACL_VIEW_IN_LIST]
      flags.forEach(flag => {
        if (aclValue & flag) {
          chips.push(aclTexts[flag] || { text: flag })
        }
      })

      return chips
    },

    filterEntities(item, queryText) {
      // Filter function for the autocomplete - searches by name and email
      const display = item.display ? item.display.toLowerCase() : ''
      const email = item.email ? item.email.toLowerCase() : ''
      const searchText = queryText ? queryText.toLowerCase() : ''

      return display.indexOf(searchText) > -1 || (email && email.indexOf(searchText) > -1)
    }
  }
}
</script>

<style scoped>
.container {
  padding: 0;
}

.avatar {
  background-color: var(--v-primary-base) !important;
}

.group-bg {
  background-color: var(--v-primary-base) !important;
}
</style>

<style>
/* Ensure autocomplete menu appears below the input */
.entity-select-menu {
  z-index: 9999 !important;
}
</style>
